import { useQuery } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { getHeaders } from '../api/apiUtils';
import { API_PATHS } from '../types/apiPaths';
/**
 * CZNA-598 — the create-screen half of the unified permission mechanism.
 *
 * <p>Every other permission question this module asks is answered by the backend on the
 * proposal itself (`ProposalSummary.actions`). "May I START a proposal of type X" is the
 * one that cannot be: there is no proposal yet to carry an answer. That is not a corner
 * case — it is precisely the shape of CZNA-591, where the create route had no gate at all
 * because there was no row to hang one off.
 *
 * <p>So it gets its own endpoint, and the answer is still the backend's. Nothing here
 * re-derives a rule from permission rows; this hook reads a list.
 *
 * <p>Served by mod-czna-authorities rather than mod-czna-users, where the original design
 * put it: only this module holds `PermissionMatrix`, and computing the answer next to the
 * raw permission rows would mean a second copy of that matrix — the duplication this
 * ticket removes, relocated rather than deleted.
 */
/**
 * CZNA-598 (blind review) — the module's standard no-store headers, the same
 * `apiUtils.getHeaders()` every `api/*Service.ts` GET already sends. This answer is
 * PER CALLER: it says what THIS user may do, so an intermediary holding on to it and
 * handing it to the next user is the one failure that matters. react-query's own
 * 5-minute cache is in-memory and per session, which is a different thing entirely.
 */
/** Same 5 minutes as the authorization context: role grants do not change mid-session. */
var STALE_TIME_MS = 5 * 60 * 1000;
var useAllowedProposalActions = function () {
    var ky = useOkapiKy();
    var _a = useQuery(['proposal-allowed-actions'], function () { return ky.get(API_PATHS.CZNA_AUTHORITIES_PROPOSALS_ALLOWED_ACTIONS, { headers: getHeaders() })
        .json(); }, { staleTime: STALE_TIME_MS }), data = _a.data, isLoading = _a.isLoading, isError = _a.isError;
    // A type the caller may not create in is ABSENT from the map, so presence alone is not
    // the test — an empty list must read as "no". The backend does not currently emit one,
    // and this does not depend on it continuing not to.
    var canCreateProposalOfType = function (authorityType) { var _a, _b; return ((_b = (_a = data === null || data === void 0 ? void 0 : data[authorityType]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0; };
    var canPublishNewProposalOfType = function (authorityType) { var _a, _b; return (_b = (_a = data === null || data === void 0 ? void 0 : data[authorityType]) === null || _a === void 0 ? void 0 : _a.includes('publish')) !== null && _b !== void 0 ? _b : false; };
    var canCreateProposal = Object.values(data !== null && data !== void 0 ? data : {})
        .some(function (actions) { var _a; return ((_a = actions === null || actions === void 0 ? void 0 : actions.length) !== null && _a !== void 0 ? _a : 0) > 0; });
    return {
        isLoading: isLoading,
        isError: isError,
        canCreateProposalOfType: canCreateProposalOfType,
        canPublishNewProposalOfType: canPublishNewProposalOfType,
        canCreateProposal: canCreateProposal,
    };
};
export default useAllowedProposalActions;
